import { register } from 'swiper/element/bundle';
import { Navigation } from 'swiper/modules';
import gsap from 'gsap';
register();

if (!customElements.get('plant-card-slider')) {
  class PlantCardSlider extends HTMLElement {
    constructor() {
      super();

      this.modalTriggers = this.querySelectorAll('[data-modal-trigger]');
      this.modalTargets = this.querySelectorAll('[data-modal-target]');
      this.closeButtons = this.querySelectorAll('[data-modal-close]');
      this.body = document.body;
      this.animationWindows = [];
      this.id = this.closest('.shopify-section')?.id.replace('shopify-section-', '');

      this.swiper = this.querySelector('swiper-container');
      this.swiperOptions = {
        modules: [Navigation],
        slidesPerView: 2.5,
        spaceBetween: 8,
        centerSlides: false,
        navigation: {
          nextEl: `.swiper-button-next-${this.id}`,
          prevEl: `.swiper-button-prev-${this.id}`,
        },
        loop: this.swiper?.getAttribute('loop') === 'true',
        breakpoints: {
          768: {
            slidesPerView: 3,
            spaceBetween: 16,
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 16,
          },
          1280: {
            slidesPerView: 5,
            spaceBetween: 16,
          },
          1440: {
            slidesPerView: 6,
            spaceBetween: 16,
          },
        },
      };

      // Add animation to all modals
      this.modalTargets.forEach((target) => {
        let modalWindow = target.querySelector('[data-modal-window]');
        this.animationWindows = [
          ...this.animationWindows,
          gsap.from(modalWindow, {
            opacity: 0,
            scale: 0,
            yPercent: 50,
            duration: 0.3,
            paused: true,
            ease: 'back.out(1)',
          }),
        ];
      });

      this.closeButtons?.forEach((close) => {
        close.addEventListener('click', this.closeModal.bind(this));
      });

      this.modalTriggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => {
          this.openModal(index);
        });
      });

      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeModal.bind(this);
      });

      window.addEventListener('resize', this.closeModal.bind(this));

      // Stop when the swiper is not found
      if (!this.swiper) {
        return;
      }

      Object.assign(this.swiper, this.swiperOptions);
      this.swiper?.initialize();

      // Call swiper with selected swiper element and options
      this.swiperInstance = this.swiper.swiper;

      /**
       * Listen to extra events when in Shopify editor
       */
      if (Shopify.designMode) {
        // Update the swiper when the section event is triggerd
        window.addEventListener('shopify:section:load', (event) => {
          this.swiperInstance.update();
          if (this.swiperOptions.navigation) {
            this.swiperInstance.navigation.init();
            this.swiperInstance.navigation.update();
          }
        });

        // When on block select go to the slide in front-end
        window.addEventListener('shopify:block:select', (event) => {
          this.swiperInstance.update();
          if (this.swiperOptions.navigation) {
            this.swiperInstance.navigation.init();
            this.swiperInstance.navigation.update();
          }
          this.handleBlockSelect(event);
        });

        // When on block deselect go to the slide in front-end
        window.addEventListener('shopify:block:deselect', (event) => {
          this.swiperInstance.update();
          if (this.swiperOptions.navigation) {
            this.swiperInstance.navigation.init();
            this.swiperInstance.navigation.update();
          }
          this.handleBlockSelect(event);
        });
      }
    }

    /**
     * Handles the theme editor block change/edit event
     * @param {Object} event
     */
    handleBlockSelect(event) {
      // Check if the slide index is set
      if (!('swiperSlideIndex' in event.target.dataset)) {
        return;
      }

      // Set the slide index based on loop settings or not
      let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
      if (this.swiperOptions.loop) {
        swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;
      }

      // Slide to slide based on the data attribute from the target
      const sliderToUpdate = event.target.closest('swiper-container');
      if (sliderToUpdate) sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 1000);
    }

    openModal(index) {
      this.animationWindows[index]?.play();
      this.modalTargets[index]?.classList.remove('hidden');
      this.body?.classList.add('modal-is-open');
    }

    getCurrentOpenModal() {
      let currentIndex = '';
      this.modalTargets.forEach((target, index) => {
        if (!target.classList.contains('hidden')) {
          currentIndex = index;
        }
      });
      return currentIndex;
    }

    closeModal() {
      this.animationWindows[this.getCurrentOpenModal()]?.reverse();
      this.body?.classList.remove('modal-is-open');
      setTimeout(() => {
        this.modalTargets[this.getCurrentOpenModal()]?.classList.add('hidden');
      }, 300);
    }
  }

  window.PlantCardSlider = PlantCardSlider;

  customElements.define('plant-card-slider', PlantCardSlider);
}
